import {take} from "rxjs/operators";
import {util} from "@joint/core";
import {NetscopeApiService, Response, Success} from "@app/services/netscope-api.service";
import {AppsGroups, AppsGroupsData, ResolvedAppsGroupsInclusion} from "@app/netscope/model/data-models";
import uuid = util.uuid;

/**
 * Loads the apps groups of a file, ready to be drawn: each of them gets a uuid, and the members of the groups
 * they include are gathered in them. Undefined when the file could not be loaded.
 */
export async function loadAppsGroupsFile(netscopeService: NetscopeApiService, file: string): Promise<AppsGroups[]> {
  let response: Response<AppsGroupsData> = await netscopeService.getAppsGroups(file).pipe(take(1)).toPromise();
  if (!(response instanceof Success)) {
    return undefined;
  }

  let appsGroups = response.element.apps_groups ?? [];
  setUuidForAppsGroups(appsGroups);
  await resolveIncludedAppsGroups(netscopeService, file, appsGroups);
  return appsGroups;
}

export function setUuidForAppsGroups(appsGroups: AppsGroups[]) {
  for (let appGroup of appsGroups) {
    let uuidValue = uuid();
    if (appGroup.metadata == undefined) {
      appGroup.metadata = {};
    }
    if (appGroup.uuid == undefined) {
      appGroup.uuid = uuidValue;
    }
    if (appGroup.uuid["uuid"] == undefined) {
      appGroup.metadata["uuid"] = uuidValue
    }
  }
}

/**
 * Spreads the members of the apps groups that are included in others. An inclusion is a cross reference: it
 * is kept by name in the apps groups file, so that a group can be included in several others without its
 * members being copied around, and by name and file when it points into another apps groups file. The
 * members are only gathered here, once every file involved is loaded.
 *
 * A group already walked is not walked twice, so that groups including each other cannot loop. The panel
 * refuses to build such a cycle, but nothing prevents a hand-written file from holding one.
 */
export async function resolveIncludedAppsGroups(netscopeService: NetscopeApiService, file: string,
                                                appsGroups: AppsGroups[]) {
  let appsGroupsByFile: Map<string, AppsGroups[]> = new Map();
  appsGroupsByFile.set(file, appsGroups);

  for (let appGroup of appsGroups) {
    // A group holding nothing but other apps groups has no members of its own
    appGroup.vms = appGroup.vms ?? [];
  }

  await loadIncludedAppsGroupsFiles(netscopeService, file, appsGroups, appsGroupsByFile);

  // The members each group lists itself, or matches by its rules, before the ones it includes are gathered in
  // its 'vms' below: a group of this file may be included by another one after it was itself resolved
  let ownMembersByGroup: Map<AppsGroups, { uuid: string }[]> = new Map(appsGroups.map((appGroup) =>
    [appGroup, (appGroup.vms ?? []).concat(appGroup.matched_vms ?? [])]));
  let ownMembersOf = (appGroup: AppsGroups): { uuid: string }[] => {
    return ownMembersByGroup.get(appGroup) ?? (appGroup.vms ?? []).concat(appGroup.matched_vms ?? []);
  };

  let appGroupIn = (file: string, name: string): AppsGroups => {
    return (appsGroupsByFile.get(file) ?? []).find((appGroup) => appGroup.name == name);
  };

  let membersOf = (appGroup: AppsGroups, file: string, visited: Set<string>) => {
    if (appGroup == undefined || visited.has(`${file}/${appGroup.name}`)) {
      return [];
    }
    visited.add(`${file}/${appGroup.name}`);

    // The members matched by the rules of the group are its own as well
    let members = ownMembersOf(appGroup);
    for (let included of appGroup.included_apps_groups ?? []) {
      // An inclusion naming no file points inside the file holding the group that carries it
      let includedFile = included.file ?? file;
      members = members.concat(membersOf(appGroupIn(includedFile, included.name), includedFile, visited));
    }

    return members;
  };

  /**
   * What the groups included by a group bring in, among the members it was itself given, and what the groups
   * they include bring in in turn. As at the first level, a member the group lists itself stays its own, and
   * one brought in by two inclusions goes to the first of them. 'ancestors' are the groups walked down to here,
   * so that groups including each other stop at the first loop.
   */
  let nestedInclusionsOf = (appGroup: AppsGroups, file: string, available: Set<string>,
                            ancestors: Set<string>): ResolvedAppsGroupsInclusion[] => {
    let claimedUuids: Set<string> = new Set(ownMembersOf(appGroup).map((member) => member.uuid));
    let inclusions: ResolvedAppsGroupsInclusion[] = [];

    for (let included of appGroup.included_apps_groups ?? []) {
      let includedFile = included.file ?? file;
      let key = `${includedFile}/${included.name}`;
      if (ancestors.has(key)) {
        continue;
      }

      let includedAppGroup = appGroupIn(includedFile, included.name);
      let members = [];
      for (let member of membersOf(includedAppGroup, includedFile, new Set(ancestors))) {
        if (available.has(member.uuid) && !claimedUuids.has(member.uuid)) {
          claimedUuids.add(member.uuid);
          members.push(member);
        }
      }

      inclusions.push({
        name: included.name,
        file: includedFile,
        members: members,
        inclusions: includedAppGroup == undefined ? [] : nestedInclusionsOf(includedAppGroup, includedFile,
          new Set(members.map((member) => member.uuid)), new Set([...ancestors, key])),
      });
    }

    return inclusions;
  };

  for (let appGroup of appsGroups) {
    if ((appGroup.included_apps_groups ?? []).length == 0 && (appGroup.matched_vms ?? []).length == 0) {
      continue;
    }

    // What each included group brings in, for the renderer to draw it as a box inside this one. A member the
    // group lists itself, or matches by its rules, stays its own, and one brought in by two inclusions goes to
    // the first of them.
    let claimedUuids: Set<string> = new Set(ownMembersOf(appGroup).map((member) => member.uuid));
    appGroup.resolvedInclusions = (appGroup.included_apps_groups ?? []).map((included) => {
      let includedFile = included.file ?? file;
      let visited: Set<string> = new Set([`${file}/${appGroup.name}`]);
      let includedMembers = [];
      for (let member of membersOf(appGroupIn(includedFile, included.name), includedFile, visited)) {
        if (!claimedUuids.has(member.uuid)) {
          claimedUuids.add(member.uuid);
          includedMembers.push(member);
        }
      }

      let includedAppGroup = appGroupIn(includedFile, included.name);
      let inclusions = includedAppGroup == undefined ? [] : nestedInclusionsOf(includedAppGroup, includedFile,
        new Set(includedMembers.map((member) => member.uuid)),
        new Set([`${file}/${appGroup.name}`, `${includedFile}/${included.name}`]));

      return {name: included.name, file: included.file, members: includedMembers, inclusions: inclusions};
    });

    let membersByUuid: Map<string, any> = new Map();
    for (let member of membersOf(appGroup, file, new Set())) {
      if (!membersByUuid.has(member.uuid)) {
        // The inherited members are copied: the renderer fills them per apps group, and a member shared by
        // two groups would otherwise be the very same object in both of them
        membersByUuid.set(member.uuid, {...member});
      }
    }

    appGroup.vms = Array.from(membersByUuid.values());
  }
}

/**
 * Loads the apps groups files the inclusions point into, and the ones those files point into in turn. The
 * displayed file is already there, and a file is never loaded twice: a cycle between two files stops as
 * soon as both of them are known.
 */
async function loadIncludedAppsGroupsFiles(netscopeService: NetscopeApiService, file: string,
                                           appsGroups: AppsGroups[], appsGroupsByFile: Map<string, AppsGroups[]>) {
  let filesToLoad = (appsGroups: AppsGroups[], file: string): string[] => {
    let files: Set<string> = new Set();

    for (let appGroup of appsGroups ?? []) {
      for (let included of appGroup.included_apps_groups ?? []) {
        let includedFile = included.file ?? file;
        if (!appsGroupsByFile.has(includedFile)) {
          files.add(includedFile);
        }
      }
    }

    return Array.from(files);
  };

  let pendingFiles = filesToLoad(appsGroups, file);

  while (pendingFiles.length > 0) {
    // The files are marked as known before they are loaded, so that a file pointing back at one being
    // loaded does not ask for it a second time
    for (let file of pendingFiles) {
      appsGroupsByFile.set(file, []);
    }

    let responses: Response<AppsGroupsData>[] = await Promise.all(pendingFiles.map((file) => {
      return netscopeService.getAppsGroups(file).pipe(take(1)).toPromise();
    }));

    let newlyPendingFiles: string[] = [];

    responses.forEach((response, index) => {
      if (!(response instanceof Success)) {
        return;
      }

      let file = pendingFiles[index];
      let appsGroups = response.element?.apps_groups ?? [];
      appsGroupsByFile.set(file, appsGroups);
      newlyPendingFiles = newlyPendingFiles.concat(filesToLoad(appsGroups, file));
    });

    pendingFiles = Array.from(new Set(newlyPendingFiles));
  }
}
